import { DEFAULT_ADS_CONFIG, EDITORIAL_AUTO_AD_PAGES } from '@/config/adExperiments.js'

const applies = (id, pathname, config) => {
  if (id === 'download_format' || id === 'editorial_side_rail') return pathname === '/baixar-visualg'
  if (id === 'editorial_responsive') return EDITORIAL_AUTO_AD_PAGES.includes(pathname)
  if (id === 'exercises_density') return pathname === '/aprender/exercicios-portugol-resolvidos-visualg'
  if (id === 'nexo_result') return pathname === '/jogos/nexo'
  if (id === 'editor_variables') return pathname === '/'
  if (id === 'learning_density') return pathname === '/' || (pathname.startsWith('/aprender') && !pathname.includes('exercicios-portugol-resolvidos'))
  if (id === 'auto_anchors' || id === 'auto_in_page') return config.autoAds.pages.includes(pathname)
  return false
}

let memoryBucket
// One random cohort number, retained within the tab. No account/device identifier.
export function adSessionBucket({ persist = true } = {}) {
  if (memoryBucket === undefined && persist) {
    try {
      const stored = sessionStorage.getItem('visualg:ads-cohort:v1')
      if (stored !== null && /^\d{1,4}$/.test(stored)) memoryBucket = Number(stored)
    } catch { /* Storage can be unavailable. Keep a stable in-memory assignment. */ }
  }
  if (memoryBucket === undefined) {
    memoryBucket = Math.floor(Math.random() * 10000)
  }
  if (persist) {
    try { sessionStorage.setItem('visualg:ads-cohort:v1', String(memoryBucket)) } catch { /* Memory fallback. */ }
  }
  return memoryBucket
}

export function resolveAdExperiment(id, policy, { ready = true, preview = false } = {}) {
  const config = policy.adConfig || DEFAULT_ADS_CONFIG
  const experiment = config.experiments[id]
  const previewTreatment = policy.mockEnabled && preview && import.meta.env.VITE_ADS_MOCK_VARIANT !== 'control'
  if (previewTreatment) return { experiment: id, variant: 'treatment' }
  if (!experiment?.enabled || (!policy.eligible && !policy.mockEnabled)) return { experiment: 'baseline', variant: 'control' }
  if (policy.pageKey && (!applies(id, policy.pageKey, config) || Object.entries(config.experiments).filter(([key, value]) => value.enabled && applies(key, policy.pageKey, config)).length > 1)) return { experiment: 'baseline', variant: 'control' }
  if (policy.mockEnabled) return { experiment: id, variant: ready && import.meta.env.VITE_ADS_MOCK_VARIANT !== 'control' ? 'treatment' : 'control' }
  const shift = [...id].reduce((hash, character) => (hash * 31 + character.charCodeAt(0)) % 10000, 0)
  const bucket = ((policy.experimentBucket ?? 9999) + shift) % 10000
  return { experiment: id, variant: ready && bucket < experiment.treatmentPercent * 100 ? 'treatment' : 'control' }
}

export function resolveAutoAds(policy, pathname) {
  const auto = (policy.adConfig || DEFAULT_ADS_CONFIG).autoAds
  if (policy.mockEnabled || !policy.eligible || policy.advertisingConsent !== 'granted' || (policy.providers && !policy.providers.includes('google')) || policy.ageTreatment !== 'UNSPECIFIED' || !auto.editorial || auto.vignettes || !auto.configurationVerified || !auto.pages.includes(pathname)) return null
  // Formats are configured in AdSense, never with invented browser parameters.
  // Run a single format at a time and require a matching reviewed panel profile.
  const id = auto.profile === 'anchors' && auto.anchors && !auto.inPage ? 'auto_anchors'
    : auto.profile === 'in_page' && auto.inPage && !auto.anchors ? 'auto_in_page' : null
  if (!id) return null
  const decision = resolveAdExperiment(id, policy)
  return decision.variant === 'treatment' ? decision : null
}

// Attribute every manual slot on an experimental page to the same cohort.
// This permits measuring lost revenue in existing units as well as the candidate.
export function resolvePageAdExperiment(policy) {
  const config = policy.adConfig || DEFAULT_ADS_CONFIG
  if (!policy.pageKey) return { experiment: 'baseline', variant: 'control' }
  const active = Object.entries(config.experiments).filter(([id, experiment]) => experiment.enabled && applies(id, policy.pageKey, config))
  if (active.length !== 1) return { experiment: 'baseline', variant: 'control' }
  const id = active[0][0]
  const unit = { download_format: config.downloadFormat === 'rectangle' ? 'downloadRectangle' : 'downloadResponsive', editorial_responsive: 'editorialResponsive', editorial_side_rail: 'editorialSideRail', nexo_result: 'nexoResult', editor_variables: 'editorVariables' }[id]
  const auto = config.autoAds
  if (id.startsWith('auto_') && (policy.advertisingConsent !== 'granted' || policy.ageTreatment !== 'UNSPECIFIED')) return { experiment: 'baseline', variant: 'control' }
  const ready = unit ? Boolean(config.units[unit]) : id.startsWith('auto_') ? auto.editorial && auto.configurationVerified && !auto.vignettes && (id === 'auto_anchors' ? auto.profile === 'anchors' && auto.anchors && !auto.inPage : auto.profile === 'in_page' && auto.inPage && !auto.anchors) : true
  return resolveAdExperiment(id, policy, { ready })
}
